/**
 * useNoteGeoJson Hook
 *
 * Converts an array of NoteMapItem objects into a GeoJSON FeatureCollection
 * for the WebGL `NoteSymbolLayer`. The `isSelected` flag drives the marker's
 * 1.2× scale-up via the layer's paint expression.
 *
 * Memoized via a `id:isSelected:color` signature so identical content avoids
 * recomputation when the upstream array reference changes.
 *
 * @module components/map/NoteSymbolLayer/hooks/useNoteGeoJson
 */

import { useMemo } from 'react';

import type { Feature, FeatureCollection, Point } from 'geojson';

import {
  MAP_CLUSTER_FEATURE_KIND_NOTE,
  type MapClusterFeatureKind,
} from '@/components/map/MapClusterLayer/featureKind';

// ===== Types =====

/**
 * Minimal note shape required to render a pin on the map. `NoteMapItem`
 * satisfies it; non-list callers (e.g. `NoteMiniMap` previews) can pass a
 * narrower object without fabricating list-only fields.
 */
export interface RenderableNote {
  id: string;
  shortId: string;
  latitude: number;
  longitude: number;
  color: string;
}

interface UseNoteGeoJsonProps {
  notes: RenderableNote[];
  selectedId: string | null;
}

export interface NoteFeatureProperties {
  /** Discriminator for the unified cluster source. */
  featureKind: MapClusterFeatureKind;
  /** Note unique identifier (GUID) */
  id: string;
  /** URL-friendly short identifier (NanoID) — used by click handler. */
  shortId: string;
  /** Per-note display color (hex, no CSS variables — WebGL cannot resolve them). */
  color: string;
  /** Whether this note is the currently selected one — drives paint-side scaling. */
  isSelected: boolean;
}

// ===== Conversion =====

function noteToFeature(
  note: RenderableNote,
  isSelected: boolean
): Feature<Point, NoteFeatureProperties> {
  return {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [note.longitude, note.latitude],
    },
    properties: {
      featureKind: MAP_CLUSTER_FEATURE_KIND_NOTE,
      id: note.id,
      shortId: note.shortId,
      color: note.color,
      isSelected,
    },
  };
}

// ===== Hook =====

export function useNoteGeoJson({
  notes,
  selectedId,
}: UseNoteGeoJsonProps): FeatureCollection<Point, NoteFeatureProperties> {
  const notesSignature = useMemo(
    () =>
      notes
        .map(
          (note) => `${note.id}:${note.id === selectedId ? 1 : 0}:${note.color}`
        )
        .join(','),
    [notes, selectedId]
  );

  return useMemo(
    () => ({
      type: 'FeatureCollection',
      features: notes.map((note) =>
        noteToFeature(note, note.id === selectedId)
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [notesSignature]
  );
}
